/*
  Isekaid Magnetic Hover System
  File: assets/isekaid-magnetic.js
*/

(() => {
  const MAGNETIC_CLASS = 'isekaid-magnetic';
  const ACTIVE_CLASS = 'is-magnetic-active';
  const INNER_CLASS = 'isekaid-magnetic__inner';

  const finePointerQuery = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  /*
    Controls how strong the movement feels.
  */
  const CONFIG = {
    /*
      Maximum button movement in pixels.
    */
    maxMovement: 5,

    /*
      Inner text moves less than the button.
    */
    innerMovementRatio: 0.22,

    /*
      Cursor can begin affecting the button slightly before
      it reaches the button's exact edge.
    */
    activationPadding: 38,

    /*
      Limits pointer updates to one browser paint cycle.
    */
    useAnimationFrame: true
  };

  const magneticElements = new Set();

  let frameRequested = false;
  let latestPointerX = 0;
  let latestPointerY = 0;

  /*
    Avoid applying decorative movement to sensitive controls.
  */
  const EXCLUDED_SELECTOR = [
    'input',
    'select',
    'textarea',
    'option',
    '[disabled]',
    '[aria-disabled="true"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="slider"]',
    '.product-form__input',
    '.quantity',
    '.quantity-selector',
    '.variant-picker',
    '.shopify-payment-button',
    '.cart-drawer',
    '.menu-drawer',
    '.predictive-search',
    '.isekaid-no-magnetic'
  ].join(',');

  const canRun = () => {
    return (
      finePointerQuery.matches &&
      !reducedMotionQuery.matches
    );
  };

  const isExcluded = (element) => {
    return Boolean(
      !element ||
      element.matches(EXCLUDED_SELECTOR) ||
      element.closest(EXCLUDED_SELECTOR)
    );
  };

  /*
    Wrap existing button contents in one inner layer.
    This lets the button and its contents move independently.
  */
  const ensureInnerLayer = (element) => {
    const existingInner = Array.from(element.children).find(
      (child) => child.classList.contains(INNER_CLASS)
    );

    if (existingInner) {
      return existingInner;
    }

    const inner = document.createElement('span');
    inner.className = INNER_CLASS;

    while (element.firstChild) {
      inner.appendChild(element.firstChild);
    }

    element.appendChild(inner);

    return inner;
  };

  const resetElement = (element) => {
    if (!element) return;

    const inner = element.querySelector(
      `:scope > .${INNER_CLASS}`
    );

    element.classList.remove(ACTIVE_CLASS);

    element.style.setProperty(
      '--isekaid-magnetic-x',
      '0px'
    );

    element.style.setProperty(
      '--isekaid-magnetic-y',
      '0px'
    );

    if (inner) {
      inner.style.setProperty(
        '--isekaid-magnetic-inner-x',
        '0px'
      );

      inner.style.setProperty(
        '--isekaid-magnetic-inner-y',
        '0px'
      );
    }
  };

  const updateElementFromPointer = (
    element,
    pointerX,
    pointerY
  ) => {
    const rect = element.getBoundingClientRect();

    const paddedLeft =
      rect.left - CONFIG.activationPadding;

    const paddedRight =
      rect.right + CONFIG.activationPadding;

    const paddedTop =
      rect.top - CONFIG.activationPadding;

    const paddedBottom =
      rect.bottom + CONFIG.activationPadding;

    const pointerIsNear =
      pointerX >= paddedLeft &&
      pointerX <= paddedRight &&
      pointerY >= paddedTop &&
      pointerY <= paddedBottom;

    if (!pointerIsNear) {
      resetElement(element);
      return;
    }

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const differenceX = pointerX - centerX;
    const differenceY = pointerY - centerY;

    const activationWidth =
      rect.width / 2 + CONFIG.activationPadding;

    const activationHeight =
      rect.height / 2 + CONFIG.activationPadding;

    const normalizedX = Math.max(
      -1,
      Math.min(1, differenceX / activationWidth)
    );

    const normalizedY = Math.max(
      -1,
      Math.min(1, differenceY / activationHeight)
    );

    /*
      Ease the movement toward the edges so it does not feel
      mechanically linear.
    */
    const easedX =
      Math.sign(normalizedX) *
      Math.pow(Math.abs(normalizedX), 1.25);

    const easedY =
      Math.sign(normalizedY) *
      Math.pow(Math.abs(normalizedY), 1.25);

    const moveX = easedX * CONFIG.maxMovement;
    const moveY = easedY * CONFIG.maxMovement;

    const inner = element.querySelector(
      `:scope > .${INNER_CLASS}`
    );

    element.classList.add(ACTIVE_CLASS);

    element.style.setProperty(
      '--isekaid-magnetic-x',
      `${moveX.toFixed(2)}px`
    );

    element.style.setProperty(
      '--isekaid-magnetic-y',
      `${moveY.toFixed(2)}px`
    );

    if (inner) {
      inner.style.setProperty(
        '--isekaid-magnetic-inner-x',
        `${(
          moveX *
          CONFIG.innerMovementRatio
        ).toFixed(2)}px`
      );

      inner.style.setProperty(
        '--isekaid-magnetic-inner-y',
        `${(
          moveY *
          CONFIG.innerMovementRatio
        ).toFixed(2)}px`
      );
    }
  };

  const updateAllElements = () => {
    frameRequested = false;

    if (!canRun()) {
      magneticElements.forEach(resetElement);
      return;
    }

    magneticElements.forEach((element) => {
      if (!element.isConnected) {
        magneticElements.delete(element);
        return;
      }

      updateElementFromPointer(
        element,
        latestPointerX,
        latestPointerY
      );
    });
  };

  const requestUpdate = () => {
    if (
      frameRequested &&
      CONFIG.useAnimationFrame
    ) {
      return;
    }

    if (!CONFIG.useAnimationFrame) {
      updateAllElements();
      return;
    }

    frameRequested = true;

    window.requestAnimationFrame(updateAllElements);
  };

  const registerElement = (
    element,
    variant = 'default'
  ) => {
    if (
      !element ||
      isExcluded(element) ||
      element.classList.contains(MAGNETIC_CLASS)
    ) {
      return;
    }

    element.classList.add(MAGNETIC_CLASS);

    if (variant === 'primary') {
      element.classList.add(
        'isekaid-magnetic--primary'
      );
    }

    if (variant === 'round') {
      element.classList.add(
        'isekaid-magnetic--round'
      );
    }

    if (variant === 'text') {
      element.classList.add(
        'isekaid-magnetic--text'
      );
    }

/*
  Round controls often depend on their original internal
  structure and inherited border radius. Move the whole
  control without wrapping its contents.
*/
if (variant !== 'round') {
  ensureInnerLayer(element);
}

magneticElements.add(element);

    element.addEventListener('pointerleave', () => {
      resetElement(element);
    });

    element.addEventListener('blur', () => {
      resetElement(element);
    });
  };

  const decoratePage = (scope = document) => {
    if (!scope?.querySelectorAll) return;

    /*
      Primary storefront buttons.
    */
    scope
      .querySelectorAll(
        [
          'a.button',
          'button.button',
          '.button:not(input)',
          '.shopify-section a[class*="button"]',
          '.shopify-section button[class*="button"]',
          '.product-form__submit',
          '[class*="quick-add"] button'
        ].join(',')
      )
      .forEach((button) => {
        registerElement(button, 'primary');
      });

    /*
      Carousel arrows and custom round controls.
    */
    scope
      .querySelectorAll(
        [
          '.isekaid-creator-carousel__arrow',
          '.isekaid-hotspots__nav-button',
          '.isekaid-hotspot',
          '.slider-button',
          '[class*="carousel"] button[aria-label]'
        ].join(',')
      )
      .forEach((button) => {
        registerElement(button, 'round');
      });

    /*
      Custom text CTAs.
    */
    scope
      .querySelectorAll(
        [
          '.isekaid-hotspots__detail-link',
          '.isekaid-creator-card__creator',
          '.isekaid-creator-card__product'
        ].join(',')
      )
      .forEach((link) => {
        registerElement(link, 'text');
      });

    /*
      Anything manually assigned the class below.
    */
    scope
      .querySelectorAll('[data-isekaid-magnetic]')
      .forEach((element) => {
        const variant =
          element.dataset.isekaidMagnetic ||
          'default';

        registerElement(element, variant);
      });

    requestUpdate();
  };

  const resetEverything = () => {
    magneticElements.forEach(resetElement);
  };

  const observeDynamicContent = () => {
    const observer = new MutationObserver(
      (mutations) => {
        const addedElements = [];

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              addedElements.push(node);
            }
          });
        });

        addedElements.forEach((element) => {
          decoratePage(element);

          if (
            element.matches?.(
              '[data-isekaid-magnetic]'
            )
          ) {
            const variant =
              element.dataset.isekaidMagnetic ||
              'default';

            registerElement(element, variant);
          }
        });
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  const initialize = () => {
    decoratePage(document);

    window.addEventListener(
      'pointermove',
      (event) => {
        latestPointerX = event.clientX;
        latestPointerY = event.clientY;
        requestUpdate();
      },
      { passive: true }
    );

    window.addEventListener(
      'scroll',
      requestUpdate,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      requestUpdate,
      { passive: true }
    );

    document.addEventListener(
      'mouseleave',
      resetEverything
    );

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        decoratePage(event.target);
      }
    );

    finePointerQuery.addEventListener?.(
      'change',
      () => {
        resetEverything();
        requestUpdate();
      }
    );

    reducedMotionQuery.addEventListener?.(
      'change',
      () => {
        resetEverything();
        requestUpdate();
      }
    );

    observeDynamicContent();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      { once: true }
    );
  } else {
    initialize();
  }
})();